import { useRef, useState, useEffect, useCallback } from 'react';
import Hls from 'hls.js';

const useVideoPlayer = (videoUrl, isOpened, onVideoEnd) => {
    const videoRef = useRef(null);
    const [isMuted, setIsMuted] = useState(true);
    const [progress, setProgress] = useState(0);
    const [reachedConditions, setReachedConditions] = useState(new Set()); // 도달 조건 기록
    const [canPlay, setCanPlay] = useState(false);

    useEffect(() => {
        if (!isOpened) {
            return;
        }

        if (videoRef.current) {
            const videoElement = videoRef.current;
            const isHls = videoUrl.endsWith('.m3u8');

            if (isHls && Hls.isSupported()) {
                const hls = new Hls();
                hls.loadSource(videoUrl);
                hls.attachMedia(videoElement);

                hls.on(Hls.Events.MANIFEST_PARSED, () => {
                    console.log('HLS manifest loaded');
                });

                return () => hls.destroy();
            } else if (!isHls) {
                videoElement.src = videoUrl;
            }
        }
    }, [videoUrl, isOpened]);

    useEffect(() => {
        const videoElement = videoRef.current;

        if (videoElement) {
            const handleCanPlay = () => {
                setCanPlay(true);
            };

            const handleEnded = () => {
                console.log('Video ended');
                if (onVideoEnd) {
                    onVideoEnd();
                }
            };

            videoElement.addEventListener('canplaythrough', handleCanPlay);
            videoElement.addEventListener('ended', handleEnded);

            return () => {
                videoElement.removeEventListener('canplaythrough', handleCanPlay);
                videoElement.removeEventListener('ended', handleEnded);
            };
        }
    }, [onVideoEnd]);

    const updateProgress = useCallback(() => {
        if (videoRef.current) {
            const progressPercentage =
                (videoRef.current.currentTime / videoRef.current.duration) * 100;
            setProgress(progressPercentage);
        }
    }, []);

    const toggleMute = () => {
        if (videoRef.current) {
            videoRef.current.muted = !videoRef.current.muted;
            setIsMuted(videoRef.current.muted);
        }
    };

    const resetPlayback = useCallback(() => {
        if (videoRef.current) {
            videoRef.current.currentTime = 0;
            setProgress(0);
            setReachedConditions(new Set()); // 도달 조건 리셋
        }
    }, []);

    const parseTime = (timeString) => {
        const [hours, minutes, seconds] = timeString.split(':').map(Number);
        return hours * 3600 + minutes * 60 + seconds;
    };

    const setOnConditionReached = useCallback(
        (condition, onReached) => {
            const videoElement = videoRef.current;
            
            if (!videoElement || !onReached) {
              return;
            }

            const handleTimeUpdate = () => {
                const currentPercentage =
                    (videoElement.currentTime / videoElement.duration) * 100;
                const currentTime = videoElement.currentTime;

                let conditionKey = condition; // 기록용 키

                if (typeof condition === 'number') {
                    // 퍼센트 기반 조건
                    if (
                        currentPercentage >= condition &&
                        !reachedConditions.has(conditionKey)
                    ) {
                        setReachedConditions((prev) => new Set(prev).add(conditionKey));
                        onReached(); // 이벤트 호출
                    }
                } else if (typeof condition === 'string') {
                    // 시간 기반 조건
                    const targetTime = parseTime(condition);
                    conditionKey = `time-${condition}`;
                    if (
                        currentTime >= targetTime &&
                        !reachedConditions.has(conditionKey)
                    ) {
                        setReachedConditions((prev) => new Set(prev).add(conditionKey));
                        onReached(); // 이벤트 호출
                    }
                }
            };

            videoElement.addEventListener('timeupdate', handleTimeUpdate);

            return () => {
                videoElement.removeEventListener('timeupdate', handleTimeUpdate);
            };
        },
        [reachedConditions]
    );

    useEffect(() => {
        if (!isOpened) {
            setCanPlay(false)
        }
    }, [isOpened])

    return {
        videoRef,
        isMuted,
        progress,
        canPlay,
        updateProgress,
        toggleMute,
        resetPlayback,
        setOnConditionReached,
    };
};

export default useVideoPlayer;